import { useEffect, useRef, useState } from 'react';
import type { Device, Item } from '@shared/types.ts';
import { formatAbsolute, formatRelative } from '@shared/time.ts';
import { deleteItem, renameItem, toggleItem } from '../store/state.ts';
import type { RowHandle } from '../hooks/useReorder.ts';
import PlatformIcon from './PlatformIcon.tsx';

interface Props {
  item: Item;
  devices: Map<string, Device>;
  handle: RowHandle;
}

export default function ItemRow({ item, devices, handle }: Props) {
  const [editing, setEditing] = useState(false);
  const [showAbsolute, setShowAbsolute] = useState(false);

  // La référence est posée sur les DEUX rendus possibles de la ligne : le relevé
  // des hauteurs, au début d'un glisser, doit couvrir toute la liste, y compris
  // une ligne en cours d'édition — sinon la géométrie est fausse pour toutes
  // celles qui la suivent.
  if (editing) {
    return <ItemEditor item={item} rowRef={handle.ref} onDone={() => setEditing(false)} />;
  }

  // Quand l'article est coché, c'est le geste de cocher qui est l'information
  // utile en magasin — pas l'ajout, souvent bien antérieur.
  const traced = item.is_checked && item.checked_at !== null
    ? { verb: 'Coché', at: item.checked_at, by: item.checked_by_device }
    : { verb: 'Ajouté', at: item.created_at, by: item.created_by_device };

  const device = traced.by ? devices.get(traced.by) : undefined;

  return (
    <li ref={handle.ref} className={`item${item.is_checked ? ' item--checked' : ''}`}>
      <button
        type="button"
        className="item__handle"
        aria-label={`Déplacer ${item.label} — flèches haut et bas au clavier`}
        title="Déplacer"
        {...handle.handleProps}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M6 3.2a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0m6.6 0a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0M6 8a1.3 1.3 0 1 1-2.6 0A1.3 1.3 0 0 1 6 8m6.6 0a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0M6 12.8a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0m6.6 0a1.3 1.3 0 1 1-2.6 0 1.3 1.3 0 0 1 2.6 0"
          />
        </svg>
      </button>

      <button
        type="button"
        className="item__toggle"
        onClick={() => void toggleItem(item)}
        aria-pressed={item.is_checked}
        aria-label={item.is_checked ? `Décocher ${item.label}` : `Cocher ${item.label}`}
      >
        <span className="item__box" aria-hidden="true" />
      </button>

      <div className="item__body">
        <div className="item__line">
          <span className="item__label">{item.label}</span>
          {item.quantity && <span className="item__quantity">{item.quantity}</span>}
        </div>

        <button
          type="button"
          className="item__trace"
          onClick={() => setShowAbsolute((value) => !value)}
          title="Afficher la date exacte"
        >
          <PlatformIcon platform={device?.platform ?? 'other'} />
          <span>
            {traced.verb} par {device?.label ?? 'un appareil inconnu'} ·{' '}
            {showAbsolute ? formatAbsolute(traced.at) : formatRelative(traced.at)}
          </span>
        </button>
      </div>

      <button
        type="button"
        className="item__edit"
        onClick={() => setEditing(true)}
        aria-label={`Modifier ${item.label}`}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M11.6 1.8a1.4 1.4 0 0 1 2 2l-.8.8-2-2zM10 3.4l2 2-6.4 6.4-2.5.5.5-2.5z"
          />
        </svg>
      </button>
    </li>
  );
}

function ItemEditor({
  item,
  rowRef,
  onDone,
}: {
  item: Item;
  rowRef: RowHandle['ref'];
  onDone: () => void;
}) {
  const [label, setLabel] = useState(item.label);
  const [quantity, setQuantity] = useState(item.quantity ?? '');
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  const save = () => {
    void renameItem(item, label, quantity || null);
    onDone();
  };

  return (
    <li ref={rowRef} className="item item--editing">
      <form
        className="item__editor"
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <input
          ref={input}
          className="item__input"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={200}
          aria-label="Libellé"
          // Échap annule sans enregistrer : la modification n'est jamais subie.
          onKeyDown={(event) => event.key === 'Escape' && onDone()}
        />
        <input
          className="item__input item__input--quantity"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          placeholder="Qté"
          maxLength={60}
          aria-label="Quantité"
          onKeyDown={(event) => event.key === 'Escape' && onDone()}
        />

        <div className="item__actions">
          <button type="submit" className="button button--small">
            Enregistrer
          </button>
          <button type="button" className="button button--small button--ghost" onClick={onDone}>
            Annuler
          </button>
          <button
            type="button"
            className="button button--small button--danger"
            onClick={() => {
              void deleteItem(item);
              onDone();
            }}
          >
            Supprimer
          </button>
        </div>
      </form>
    </li>
  );
}
